import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Shield, Edit, Save, X, AlertCircle, Plus } from 'lucide-react'
import { ImageUpload } from './image-upload'

interface VillageChief {
  id: string
  name: string
  title: string
  photo: string
  phone: string
  email: string
  address: string
  message: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export function VillageChiefManager() {
  const [chief, setChief] = useState<VillageChief | null>(null)
  const [allChiefs, setAllChiefs] = useState<VillageChief[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [editingChief, setEditingChief] = useState<VillageChief | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    photo: '',
    phone: '',
    email: '',
    address: '',
    message: '',
    is_active: true
  })

  useEffect(() => {
    fetchChief()
  }, [])

  const fetchChief = async () => {
    try {
      // Fetch all chiefs
      const { data: allChiefsData, error: allError } = await supabase
        .from('village_chief')
        .select('*')
        .order('created_at', { ascending: false })

      if (allError) throw allError

      setAllChiefs(allChiefsData || [])

      // Find the active chief
      const activeChief = allChiefsData?.find(c => c.is_active) || null
      setChief(activeChief)

      if (activeChief) {
        setFormData({
          name: activeChief.name,
          title: activeChief.title,
          photo: activeChief.photo,
          phone: activeChief.phone,
          email: activeChief.email,
          address: activeChief.address,
          message: activeChief.message,
          is_active: activeChief.is_active
        })
      }
    } catch (err) {
      setError('Failed to fetch village chief data')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')

    try {
      if (isAddingNew) {
        // Create new chief and make it active
        // First, deactivate all existing chiefs
        if (allChiefs.length > 0) {
          const { error: deactivateError } = await supabase
            .from('village_chief')
            .update({ is_active: false })

          if (deactivateError) throw deactivateError
        }

        // Create new active chief
        const { error } = await supabase
          .from('village_chief')
          .insert({
            ...formData,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (error) throw error
        setIsAddingNew(false)
      } else if (chief || editingChief) {
        // Update existing chief
        const chiefId = editingChief ? editingChief.id : chief!.id
        const { error } = await supabase
          .from('village_chief')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', chiefId)

        if (error) throw error
        setEditingChief(null)
      }

      await fetchChief()
      setIsEditing(false)
    } catch (err) {
      setError('Failed to save village chief data')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (chief) {
      setFormData({
        name: chief.name,
        title: chief.title,
        photo: chief.photo,
        phone: chief.phone,
        email: chief.email,
        address: chief.address,
        message: chief.message,
        is_active: chief.is_active
      })
    }
    setIsEditing(false)
    setIsAddingNew(false)
    setEditingChief(null)
  }

  const handleAddNew = () => {
    setFormData({
      name: '',
      title: '',
      photo: '',
      phone: '',
      email: '',
      address: '',
      message: '',
      is_active: true
    })
    setIsAddingNew(true)
    setIsAddDialogOpen(true)
  }

  const handleDisableCurrent = async () => {
    if (!chief) return

    try {
      const { error } = await supabase
        .from('village_chief')
        .update({ is_active: false })
        .eq('id', chief.id)

      if (error) throw error
      await fetchChief()
    } catch (err) {
      setError('Failed to disable current chief')
    }
  }

  const handleEditChief = (chiefItem: VillageChief) => {
    setEditingChief(chiefItem)
    setFormData({
      name: chiefItem.name,
      title: chiefItem.title,
      photo: chiefItem.photo,
      phone: chiefItem.phone,
      email: chiefItem.email,
      address: chiefItem.address,
      message: chiefItem.message,
      is_active: chiefItem.is_active
    })
    setIsEditDialogOpen(true)
  }

  const handleSaveFromDialog = async () => {
    setSaving(true)
    setError('')

    try {
      if (!editingChief) return

      // If making this chief active, deactivate all others first
      if (formData.is_active) {
        // Deactivate each active chief individually to avoid issues with update without filter
        for (const chiefItem of allChiefs) {
          if (chiefItem.is_active && chiefItem.id !== editingChief.id) {
            const { error: deactivateError } = await supabase
              .from('village_chief')
              .update({ is_active: false })
              .eq('id', chiefItem.id)

            if (deactivateError) {
              throw new Error(`Failed to deactivate existing chief: ${deactivateError.message}`)
            }
          }
        }
      }

      const { error } = await supabase
        .from('village_chief')
        .update({
          ...formData,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingChief.id)

      if (error) throw error

      setIsEditDialogOpen(false)
      setEditingChief(null)
      await fetchChief()
    } catch (err) {
      setError('Failed to save village chief data')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveFromAddDialog = async () => {
    setSaving(true)
    setError('')

    try {
      // If making this chief active, deactivate all existing chiefs first
      if (formData.is_active && allChiefs.length > 0) {
        // Deactivate each chief individually to avoid issues with update without filter
        for (const chiefItem of allChiefs) {
          if (chiefItem.is_active) {
            const { error: deactivateError } = await supabase
              .from('village_chief')
              .update({ is_active: false })
              .eq('id', chiefItem.id)

            if (deactivateError) {
              throw new Error(`Failed to deactivate existing chief: ${deactivateError.message}`)
            }
          }
        }
      }

      // Create new chief
      const { data, error } = await supabase
        .from('village_chief')
        .insert({
          name: formData.name,
          title: formData.title,
          photo: formData.photo,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          message: formData.message,
          is_active: formData.is_active,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()

      if (error) {
        throw new Error(`Failed to add chief: ${error.message}`)
      }

      setIsAddDialogOpen(false)
      setIsAddingNew(false)
      await fetchChief()
    } catch (err: any) {
      setError(err.message || 'Failed to add new chief')
    } finally {
      setSaving(false)
    }
  }

  const handleMakeActive = async (chiefItem: VillageChief) => {
    try {
      // First deactivate all active chiefs
      for (const chief of allChiefs) {
        if (chief.is_active && chief.id !== chiefItem.id) {
          const { error: deactivateError } = await supabase
            .from('village_chief')
            .update({ is_active: false })
            .eq('id', chief.id)

          if (deactivateError) {
            throw new Error(`Failed to deactivate chief: ${deactivateError.message}`)
          }
        }
      }

      // Then activate the selected chief
      const { error: activateError } = await supabase
        .from('village_chief')
        .update({ is_active: true })
        .eq('id', chiefItem.id)

      if (activateError) {
        throw new Error(`Failed to activate chief: ${activateError.message}`)
      }
      await fetchChief()
    } catch (err: any) {
      setError(err.message || 'Failed to make chief active')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading village chief data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Village Chief Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage the village chief's information and profile
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto h-12 px-6 rounded-xl font-semibold transition-all duration-200 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Chief
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Chiefs Management Section */}
      {allChiefs.length > 0 && (
        <Card className="bg-white/95 backdrop-blur-md border-0 shadow-xl rounded-2xl overflow-hidden mt-8">
          <CardHeader className="bg-gradient-to-r from-slate-600 to-slate-700 text-white p-6">
            <CardTitle className="text-xl font-bold text-white flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              All Chiefs ({allChiefs.length})
            </CardTitle>
            <CardDescription className="text-slate-200">
              Manage all village chiefs and their status
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {allChiefs.map((chiefItem) => (
                <div 
                  key={chiefItem.id}
                  className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 hover:shadow-lg ${
                    chiefItem.is_active 
                      ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' 
                      : 'border-slate-200 bg-gradient-to-r from-slate-50 to-gray-50'
                  }`}
                >
                  {/* Chief Image and Name */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden border-3 border-white shadow-lg">
                        {chiefItem.photo ? (
                          <img 
                            src={chiefItem.photo} 
                            alt={chiefItem.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Hide broken image and show fallback
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                            <Shield className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      {chiefItem.is_active && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">{chiefItem.name}</h3>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {!chiefItem.is_active && (
                      <Button
                        onClick={() => handleMakeActive(chiefItem)}
                        className="h-10 px-5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-200"
                      >
                        Active
                      </Button>
                    )}
                    {chiefItem.is_active && (
                      <Button
                        onClick={() => handleDisableCurrent()}
                        className="h-10 px-5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-xl font-semibold transition-all duration-200"
                      >
                        Inactive
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEditChief(chiefItem)}
                      variant="outline"
                      className="h-10 px-5 border-2 border-blue-300 hover:bg-blue-50 hover:border-blue-400 text-blue-600 rounded-xl font-semibold transition-all duration-200"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add New Chief Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              Add New Chief
            </DialogTitle>
            <DialogDescription>
              Add a new village chief to replace the current one
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Chief Image Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-800">Chief Photo</Label>
              <ImageUpload
                currentImage={formData.photo}
                onImageChange={(imageUrl) => setFormData({ ...formData, photo: imageUrl })}
                folder="village-chief"
                label=""
                description=""
              />
            </div>

            {/* Name and Title */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-name" className="text-sm font-semibold text-slate-800">
                  Full Name
                </Label>
                <Input
                  id="add-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-title" className="text-sm font-semibold text-slate-800">
                  Title/Position
                </Label>
                <Input
                  id="add-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Village Sarpanch"
                  className="h-11"
                />
              </div>
            </div>

            {/* Phone and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-phone" className="text-sm font-semibold text-slate-800">
                  Phone Number
                </Label>
                <Input
                  id="add-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-email" className="text-sm font-semibold text-slate-800">
                  Email Address
                </Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="chief@village.gov.in"
                  className="h-11"
                />
              </div>
            </div>

            {/* Office Address */}
            <div className="space-y-2">
              <Label htmlFor="add-address" className="text-sm font-semibold text-slate-800">
                Office Address
              </Label>
              <Input
                id="add-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Village Office, Main Road, Village Name"
                className="h-11"
              />
            </div>

            {/* Welcome Message */}
            <div className="space-y-2">
              <Label htmlFor="add-message" className="text-sm font-semibold text-slate-800">
                Welcome Message
              </Label>
              <Textarea
                id="add-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter a welcome message for the village chief..."
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Active Status Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-0.5">
                <Label htmlFor="add-active" className="text-sm font-semibold text-slate-800">
                  Chief Status
                </Label>
                <p className="text-xs text-slate-600">
                  {formData.is_active 
                    ? 'This chief will be set as active and all others will be deactivated' 
                    : 'This chief will be added as inactive'}
                </p>
              </div>
              <Switch
                id="add-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={saving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveFromAddDialog}
              disabled={saving}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Adding...' : 'Add Chief'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Chief Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Edit Chief Information
            </DialogTitle>
            <DialogDescription>
              Update the village chief details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Chief Image Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-800">Chief Photo</Label>
              <ImageUpload
                currentImage={formData.photo}
                onImageChange={(imageUrl) => setFormData({ ...formData, photo: imageUrl })}
                folder="village-chief"
                label=""
                description=""
              />
            </div>

            {/* Name and Title */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-sm font-semibold text-slate-800">
                  Full Name
                </Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-title" className="text-sm font-semibold text-slate-800">
                  Title/Position
                </Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Village Sarpanch"
                  className="h-11"
                />
              </div>
            </div>

            {/* Phone and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone" className="text-sm font-semibold text-slate-800">
                  Phone Number
                </Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email" className="text-sm font-semibold text-slate-800">
                  Email Address
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="chief@village.gov.in"
                  className="h-11"
                />
              </div>
            </div>

            {/* Office Address */}
            <div className="space-y-2">
              <Label htmlFor="edit-address" className="text-sm font-semibold text-slate-800">
                Office Address
              </Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Village Office, Main Road, Village Name"
                className="h-11"
              />
            </div>

            {/* Welcome Message */}
            <div className="space-y-2">
              <Label htmlFor="edit-message" className="text-sm font-semibold text-slate-800">
                Welcome Message
              </Label>
              <Textarea
                id="edit-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter a welcome message for the village chief..."
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Active Status Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-0.5">
                <Label htmlFor="edit-active" className="text-sm font-semibold text-slate-800">
                  Chief Status
                </Label>
                <p className="text-xs text-slate-600">
                  {formData.is_active 
                    ? 'This chief will be set as active and all others will be deactivated' 
                    : 'This chief will be set as inactive'}
                </p>
              </div>
              <Switch
                id="edit-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={saving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveFromDialog}
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
